import React from 'react';
import { Divider, Dropdown, DropdownMenu, List } from 'semantic-ui-react';

import { SidebarContext } from '.';
import {
  ACCOUNT_PATH,
  BILLING_PATH,
  BYOC_PATH,
  CONFIGURATIONS_PATH,
  ROOT_PATH,
  SidebarTab,
  STATISTICS_PATH,
} from '../../../constants/paths/internal';
import { CONFIG } from '../../../meta/types/config';
import { SidebarStyle } from '../../../styles/Sidebar';
import SidebarListItem from './Item';
import News from './News';
import { getSkin } from '../../../lib/skin';

type Props = {
  setSidebarCollapsed: (collapsed: boolean) => void;
  newsVisible: boolean;
  toggleNews: () => void;
};

const IS_BYOC_ENABLED = CONFIG.byocEnabled === undefined ? true : CONFIG.byocEnabled;

const Sidebar = ({ setSidebarCollapsed, newsVisible, toggleNews }: Props) => {
  return (
    <SidebarContext.Consumer>
      {({ sidebarCollapsed, activeTab, rootManaging, hasUnseenNews, shAccount }) => {
        const hasActiveAccount = shAccount !== undefined;

        return (
          <SidebarStyle sidebarCollapsed={sidebarCollapsed} className="sidebar">
            <List className="sidebar-list">
              <SidebarListItem
                icon="grid layout"
                content="Dashboard"
                active={activeTab === SidebarTab.DASHBOARD}
                to={ROOT_PATH}
                popupContent={sidebarCollapsed ? 'Dashboard' : undefined}
              />

              <SidebarListItem
                icon="cubes"
                content="Configuration Utility"
                active={activeTab === SidebarTab.CONFIGURATOR}
                to={CONFIGURATIONS_PATH}
                disabled={rootManaging ? false : !hasActiveAccount}
                popupContent={
                  !hasActiveAccount
                    ? 'Your account has expired. You are not allowed to manage your configurations.'
                    : 'Configuration Utility'
                }
              />

              {IS_BYOC_ENABLED && (
                <SidebarListItem
                  icon="map outline"
                  content="Bring your own COG"
                  active={activeTab === SidebarTab.BYOC}
                  to={BYOC_PATH}
                  popupContent={
                    !hasActiveAccount
                      ? 'Your account has expired. You are not allowed to bring your own configurations.'
                      : 'Bring your own COG'
                  }
                />
              )}

              <Divider />

              <SidebarListItem
                icon="settings"
                content="User settings"
                active={activeTab === SidebarTab.ACCOUNT_SETTINGS}
                to={ACCOUNT_PATH}
                popupContent={sidebarCollapsed ? 'User settings' : undefined}
              />

              <SidebarListItem
                icon="pie graph"
                content="Statistics"
                active={activeTab === SidebarTab.STATISTICS}
                to={STATISTICS_PATH}
                popupContent={sidebarCollapsed ? 'Statistics' : undefined}
              />

              <SidebarListItem
                icon="credit card outline"
                content="Billing"
                active={activeTab === SidebarTab.BILLING}
                to={BILLING_PATH}
                popupContent={sidebarCollapsed ? 'Billing' : undefined}
              />
            </List>

            <div className="sidebar__bottom">
              <List>
                <SidebarListItem
                  icon="question"
                  active={false}
                  content="Help"
                  dropdownMenu={
                    <DropdownMenu style={{ left: '15px' }}>
                      {getSkin().helpLinks.map(helpLink => (
                        <Dropdown.Item
                          text={helpLink.text}
                          onClick={() => window.open(helpLink.link, '_blank')}
                          key={helpLink.text}
                        />
                      ))}
                    </DropdownMenu>
                  }
                  popupContent={sidebarCollapsed ? 'Help' : undefined}
                />
                <SidebarListItem
                  icon="bullhorn"
                  additionalIcon={
                    hasUnseenNews ? (
                      <i
                        className="circle top right corner icon"
                        style={{ color: 'palevioletred' }}
                      />
                    ) : undefined
                  }
                  active={newsVisible}
                  content="What's new"
                  onClick={toggleNews}
                  popupContent={sidebarCollapsed ? "What's new" : undefined}
                />
                <SidebarListItem
                  icon={sidebarCollapsed ? 'angle double right' : 'angle double left'}
                  active={false}
                  content="Collapse Sidebar"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  popupContent={sidebarCollapsed ? 'Expand sidebar' : undefined}
                />
              </List>
            </div>
            {newsVisible && <News onClose={toggleNews} />}
          </SidebarStyle>
        );
      }}
    </SidebarContext.Consumer>
  );
};

export default React.memo(Sidebar);
